<script lang="ts">
	import type { HTMLInputAttributes } from "svelte/elements";
	import { Locale, type LocalizedString } from "@core/schema/Locale.js";
	import { emailPattern } from "@core/utils/email.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";

	const { ...rest }: HTMLInputAttributes = $props();
	const { tenant } = getGlobalTemplateProps();

	const type = $derived(rest.type ?? "text");
	const isCheckboxRadio = $derived(["checkbox", "radio"].includes(type));
	const customPattern: Partial<Record<NonNullable<HTMLInputAttributes["type"]>, string>> = {
		email: emailPattern,
	};
	const pattern = $derived(rest.pattern ?? customPattern[type]);

	const customValidationMessage: Partial<Record<NonNullable<HTMLInputAttributes["type"]>, LocalizedString>> = {
		email: {
			[Locale.cs]: "E-mailová adresa",
			[Locale.en]: "E-mail address",
		},
		url: {
			[Locale.cs]: "URL adresa (včetně https://)",
			[Locale.en]: "URL address (including https://)",
		},
	};

	// Text / date / etc. — clearly visible on white cards: a 2px hairline that
	// turns blue on focus with a soft blue ring; red border for invalid.
	const textClass =
		"min-w-0 min-h-[3.125rem] bg-white text-ink-900 placeholder:text-ink-400 " +
		"border-2 border-ink-200 rounded-full py-3 px-4 transition-colors duration-200 " +
		"hover:border-ink-300 focus:border-secondary focus:outline-none focus:ring-4 focus:ring-secondary/20 " +
		"read-only:bg-ink-50 read-only:border-ink-200 read-only:text-ink-600 read-only:focus:ring-0 read-only:focus:border-ink-200 " +
		"disabled:opacity-60 disabled:bg-ink-50 " +
		"[&:user-invalid]:border-status-fault [&:user-invalid]:focus:ring-status-fault/20";

	// Checkbox / radio — a real, visible control: 20px, 2px border, fills blue
	// with a white mark when checked.
	const boxClass =
		"peer relative shrink-0 appearance-none h-5 w-5 rounded-full border-2 border-ink-300 bg-white cursor-pointer " +
		"transition-colors duration-150 hover:border-ink-400 " +
		"checked:border-secondary checked:bg-secondary checked:hover:border-secondary-600 checked:hover:bg-secondary-600 " +
		"focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-secondary/25 " +
		"disabled:opacity-50 disabled:cursor-not-allowed " +
		"before:absolute before:inset-0 before:m-auto before:block before:h-2 before:w-2 before:rounded-full " +
		"before:bg-white before:scale-0 before:transition-transform before:duration-150 checked:before:scale-100";

	function validate({ currentTarget }: Event & { currentTarget: EventTarget & HTMLInputElement }): void {
		currentTarget.setCustomValidity("");

		if (currentTarget.validity.valid) {
			currentTarget.setCustomValidity("");
		} else {
			const customMessage = customValidationMessage[type]?.[tenant.locale];
			if (customMessage) {
				currentTarget.setCustomValidity(customMessage);
			}
		}
	}
</script>

<input
	{...rest}
	class={[isCheckboxRadio ? boxClass : textClass, rest.class]}
	{pattern}
	oninput={(event: Event & { currentTarget: EventTarget & HTMLInputElement }): void => {
		validate(event);
		rest.oninput?.(event);
	}}
	oninvalid={(event: Event & { currentTarget: EventTarget & HTMLInputElement }): void => {
		validate(event);
		rest.oninvalid?.(event);
	}}
/>
